import * as React from "react";
import { Button, Card, Modal, notification } from "antd";
import { CardContent } from "../../components/antd/card/CardContent";
import { CardTitle } from "../../components/antd/card/CardTitle";
import { CaretRightOutlined } from "@ant-design/icons";
import { BillingAccount, BillingUserRole } from "../../schema/types/account";
import { request } from "../../services/cpln";
import { getBillingErrorMessage } from "../../services/utils";
import { CardCollapsibleContent } from "../../components/antd/card/CardCollapsibleContent";
import { loadStripe, Stripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import { PaymentMethodAdd } from "./add";

const stripePromise: Promise<Stripe | null> = loadStripe(import.meta.env.VITE_STRIPE_PUBLIC_KEY as string);

interface Props {
  account: BillingAccount;
  roles: BillingUserRole[];
  fetchPaymentMethods: any;
}
export const PaymentMethods: React.FC<Props> = ({ account, roles, fetchPaymentMethods }) => {
  const [isAddCollapsed, setIsAddCollapsed] = React.useState(true);

  const [isLoadingMakeDefault, setIsLoadingMakeDefault] = React.useState(false);
  const [paymentMethodIdToMakeDefault, setPaymentMethodIdToMakeDefault] = React.useState("");
  const [isLoadingDelete, setIsLoadingDelete] = React.useState(false);
  const [paymentMethodIdToDelete, setPaymentMethodIdToDelete] = React.useState("");
  const [paymentMethodAddSectionKey, setPaymentMethodAddSectionKey] = React.useState(0);
  function triggerPaymentMethodAddSection() {
    setPaymentMethodAddSectionKey((x) => x + 1);
  }
  React.useEffect(() => {
    if (!isAddCollapsed) {
      triggerPaymentMethodAddSection();
    }
  }, [isAddCollapsed]);

  async function paymentMethodMakeDefault() {
    try {
      setIsLoadingMakeDefault(true);
      await request({
        method: "put",
        service: "billing-ng",
        url: `/account/${account.id}/default-payment/${paymentMethodIdToMakeDefault}`,
      });
      await fetchPaymentMethods();
      setIsLoadingMakeDefault(false);
      setPaymentMethodIdToMakeDefault("");
    } catch (e) {
      setIsLoadingMakeDefault(false);
      notification.warning({
        message: "Failed",
        description: getBillingErrorMessage(e),
      });
    }
  }

  async function paymentMethodDelete() {
    try {
      setIsLoadingDelete(true);
      await request({
        method: "delete",
        service: "billing-ng",
        url: `/account/${account.id}/payment/${paymentMethodIdToDelete}`,
        body: {},
      });
      await fetchPaymentMethods();
      setIsLoadingDelete(false);
      setPaymentMethodIdToDelete("");
    } catch (e) {
      setIsLoadingDelete(false);
      notification.warning({
        message: "Failed",
        description: getBillingErrorMessage(e),
      });
    }
  }

  return (
    <>
      <div className="cpln-form-section cpln-ant-card-container">
        <Card>
          <CardTitle>
            <div className={`flex items-center`}>
              <div className={`flex items-baseline gap-2`}>
                <span>Payment Methods</span>
              </div>
              <div className="flex-grow" />
            </div>
          </CardTitle>
          <CardContent collapsed={false}>
            <div className="flex gap-4 flex-wrap">
              {(account.paymentMethods || []).map((pm) => (
                <div key={pm.id} className="rounded border p-4" data-testid={pm.expiration} style={{ width: 350 }}>
                  <div className="pb-4">
                    <div className="flex justify-between">
                      <div className="font-semibold">{pm.brand?.toUpperCase()}</div>
                      <div>
                        **** **** ****<span className="ml-1 font-semibold">{pm.last4}</span>
                      </div>
                    </div>
                    <div className="flex justify-end">
                      <div>
                        Expires <span className="font-semibold">{pm.expiration}</span>
                      </div>
                    </div>
                  </div>
                  <div className="border-t pt-4 flex items-center justify-end">
                    {pm.default ? (
                      <div className="font-semibold">Default</div>
                    ) : roles.includes("billing_admin") ? (
                      <>
                        <Button
                          htmlType={`button`}
                          type={"primary"}
                          size={"small"}
                          className="mr-2"
                          disabled={!!paymentMethodIdToMakeDefault || !!paymentMethodIdToDelete}
                          loading={paymentMethodIdToMakeDefault === pm.id && isLoadingMakeDefault}
                          onClick={() => setPaymentMethodIdToMakeDefault(pm.id)}
                        >
                          Make Default
                        </Button>
                        <Button
                          htmlType={`button`}
                          type={"default"}
                          danger
                          size={"small"}
                          disabled={!!paymentMethodIdToMakeDefault || !!paymentMethodIdToDelete}
                          loading={paymentMethodIdToDelete === pm.id && isLoadingDelete}
                          onClick={() => setPaymentMethodIdToDelete(pm.id)}
                        >
                          Delete
                        </Button>
                      </>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
            {roles.includes("billing_admin") ? (
              <>
                <CardTitle>
                  <div className={`flex items-baseline mt-8`}>
                    <div
                      className={`flex items-baseline gap-2 color-link-hover cursor-pointer`}
                      onClick={() => setIsAddCollapsed((x) => !x)}
                    >
                      <CaretRightOutlined rotate={isAddCollapsed ? 0 : 90} className={`self-center`} />
                      <span>Add</span>
                    </div>
                    <div className="flex-grow" />
                  </div>
                </CardTitle>
                <div key={paymentMethodAddSectionKey}>
                  <CardCollapsibleContent collapsed={isAddCollapsed}>
                    <Elements stripe={stripePromise}>
                      <PaymentMethodAdd
                        account={account}
                        fetchPaymentMethods={fetchPaymentMethods}
                        roles={roles}
                        onDone={() => setIsAddCollapsed(true)}
                      />
                    </Elements>
                  </CardCollapsibleContent>
                </div>
              </>
            ) : null}
          </CardContent>
        </Card>
      </div>
      {paymentMethodIdToMakeDefault ? (
        <Modal
          title="Make Payment Method Default"
          open={true}
          destroyOnHidden
          onCancel={() => {
            setPaymentMethodIdToMakeDefault("");
          }}
          onOk={() => {
            paymentMethodMakeDefault();
          }}
          cancelButtonProps={{ danger: true, type: "default", disabled: isLoadingMakeDefault }}
          okButtonProps={{ type: "primary", disabled: isLoadingMakeDefault, loading: isLoadingMakeDefault }}
        >
          Are you sure you want to make this payment method the default?
        </Modal>
      ) : null}
      {paymentMethodIdToDelete ? (
        <Modal
          title="Delete Payment Method"
          open={true}
          destroyOnHidden
          onCancel={() => {
            setPaymentMethodIdToDelete("");
          }}
          onOk={() => {
            paymentMethodDelete();
          }}
          cancelButtonProps={{ danger: true, type: "default", disabled: isLoadingDelete }}
          okButtonProps={{ type: "primary", disabled: isLoadingDelete, loading: isLoadingDelete }}
        >
          Are you sure you want to delete this payment method?
        </Modal>
      ) : null}
    </>
  );
};
